import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { EntryDetails } from '@/api/entries/types/types';
import { EventInfo } from '@/api/events/types/types';
import { Button } from '@/components/ButtonComponent';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useCountdownSeconds } from '@/hooks/use-countdown';
import { formatTime } from '@/hooks/use-format-period';
import { add } from 'date-fns';

import { useCancelEntry } from './CancelEntryProvider';

type CancelEntryButtonProps = {
    entry: EntryDetails;
    events: EventInfo[];
    setHideCancelButton: (hide: boolean) => void;
    onExpired?: () => void;
};

export const CancelEntryButton = memo(({ entry, events, setHideCancelButton, onExpired }: CancelEntryButtonProps) => {
    const { t } = useTranslation('cancel_entry');

    const { showCancelEntryModal, dismissCancelEntryModal } = useCancelEntry();
    const hasExpired = useRef(false);

    const targetDate = useMemo(() => {
        if (!entry?.createdDate || !entry?.cancelTimeFrame) {
            return new Date();
        }

        const createdDate = Date.parse(entry.createdDate);
        return add(createdDate, { minutes: entry.cancelTimeFrame / 60 });
    }, [entry?.createdDate, entry?.cancelTimeFrame]);

    const timeLeft = useCountdownSeconds(targetDate);

    useEffect(() => {
        if (timeLeft <= 0 && entry?.createdDate && !hasExpired.current) {
            hasExpired.current = true;
            setHideCancelButton(true);
            dismissCancelEntryModal();
            onExpired?.();
        }
    }, [dismissCancelEntryModal, entry, onExpired, setHideCancelButton, timeLeft]);

    return (
        <Button
            testID="cancelEntryButton"
            label={t('cancel_entry', { timeLeft: formatTime(timeLeft) })}
            hierarchy={'subtle'}
            onPress={() => {
                BetrAnalytics.trackEvent(AnalyticsEvent.CANCEL_ENTRY, {
                    timeLeft: formatTime(timeLeft),
                });
                showCancelEntryModal({ entry, events });
            }}
        />
    );
});
